import {ConfigurableComponent, LogoImageData} from '@twpub/core/models';
import {ComponentCategory} from '../../enums';
import {ComponentConfiguration} from '../index';

export enum ClientConfigurationScheme {
  DEFAULT = 'default',
}

export class ClientConfiguration {
  id: number;
  name: string = '<no name>';
  version: number = 0;
  scheduledSyncHours: number = 0;
  scheme: string = ClientConfigurationScheme.DEFAULT;
  css?: string;
  defaultConfig: boolean = false;
  props: { [key: string]: string } = {};
  fontUrls: string[] = [];
  fontIds: number[] = [];
  cssVariables: { [key: string]: string } = {};
  logoImageData?: LogoImageData;
  // Map from wrapper component type to component configuration
  _componentConfigurationMap = new Map<ComponentCategory | string, ComponentConfiguration<ConfigurableComponent>>();

  constructor(id: number, name: string, scheme: string) {
    this.id = id;
    this.name = name;
    this.scheme = scheme;
  }

  clone(): ClientConfiguration {
    const config = new ClientConfiguration(this.id, this.name, this.scheme);
    config.version = this.version;
    config.css = this.css;
    config.scheduledSyncHours = this.scheduledSyncHours;
    config.defaultConfig = this.defaultConfig;
    config.props = {...this.props};
    config.fontUrls = [...this.fontUrls];
    config.fontIds = [...this.fontIds];
    config.cssVariables = {...this.cssVariables};
    config.logoImageData = this.logoImageData ? {...this.logoImageData} : undefined;
    for (const [compCategory, compConfig] of this.componentConfigurationMap.entries()) {
      config.setConfiguration(compCategory, compConfig)
    }
    return config;
  }

  setConfiguration(compCategory: ComponentCategory | string, config: ComponentConfiguration<ConfigurableComponent>) {
    this._componentConfigurationMap.set(compCategory, config);
  }

  /**
   * Get configuration for component type. If no configuration is found, throw an error.
   * @param compCategory component type
   */
  getConfiguration(compCategory: ComponentCategory | string): ComponentConfiguration<ConfigurableComponent> {
    const config = this.getConfigurationSafe(compCategory);
    if (!config) {
      throw new Error('No configuration for component type ' + compCategory)
      // todo ej error, skapa ny config med första configurableComponent, ev i anropande component
    }
    return config;
  }

  /**
   * Get configuration for component type. If no configuration is found, return undefined.
   * @param compCategory component type
   */
  getConfigurationSafe(compCategory: ComponentCategory | string): ComponentConfiguration<ConfigurableComponent> | undefined {
    return this._componentConfigurationMap.get(compCategory);
  }

  get componentConfigurationMap(): Map<ComponentCategory | string, ComponentConfiguration<ConfigurableComponent>> {
    return this._componentConfigurationMap;
  }

  toObject() {
    const obj: any = {
      id: this.id,
      name: this.name,
      scheduledSyncHours: this.scheduledSyncHours,
      scheme: this.scheme,
      css: this.css,
      defaultConfig: this.defaultConfig,
      props: this.props,
      fontUrls: this.fontUrls,
      fontIds: this.fontIds,
      cssVariables: this.cssVariables,
      componentConfig: Object.assign({}, ...Array.from(this.componentConfigurationMap.entries())
        .filter(([_, compConfig]) => !!compConfig)
        .map(([compCategory, compConfig]) => {
          const configValues = Array.from(compConfig.config.entries());
          const configObj = {
            componentType: compConfig.componentType,
            config: Object.assign({}, ...configValues.map(([k, v]) => ({[k]: v})))
          }
          return {[compCategory]: configObj};
        }))
    };
    if (this.logoImageData) {
      obj.logoImageData = this.logoImageData;
    }
    return obj;
  }
}
